import React from 'react';
import styled from 'styled-components';
import CalendarIcon from 'assets/calendar';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useGlobalDateLocale } from 'context/DateLocaleContext';

interface DateInputProps extends React.HTMLAttributes<HTMLDivElement> {
  date: Date | null;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  id?: string;
  dateFormat?: string;
}

const DateInput = React.forwardRef<HTMLDivElement, DateInputProps>(
  (
    {
      date,
      onClick,
      onKeyDown,
      placeholder,
      label,
      disabled = false,
      id,
      dateFormat = 'MMM d',
      ...restProps
    },
    ref,
  ) => {
    const { t: translate } = useTranslation();
    const { locale } = useGlobalDateLocale();

    const baseLabel = label || translate('selectDate');
    let displayValue = placeholder || '';
    // Lead the accessible name with the visible text so speech-control users can
    // target the control by what they see (WCAG 2.5.3 Label in Name).
    let ariaLabel = displayValue ? `${displayValue}, ${baseLabel}` : baseLabel;

    if (date) {
      displayValue = format(date, dateFormat, { locale });
      // Prefix the visible text, then include the full date for screen readers.
      const fullDateText = format(date, 'EEEE, MMMM d, yyyy', { locale });
      ariaLabel = `${displayValue}, ${baseLabel}, ${fullDateText}`;
    }

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;
      onClick?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Call original onKeyDown first if it exists
      onKeyDown?.(e);

      if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        e.stopPropagation();
        // Trigger a click on the element
        e.currentTarget.click();
      }
    };

    return (
      <DateInputContainer>
        <VisuallyHidden
          id={id ? `${id}-label` : undefined}
          aria-live="polite"
          aria-atomic="true"
        >
          {ariaLabel}
        </VisuallyHidden>
        <InputWrapper
          {...restProps}
          ref={ref}
          role="button"
          disabled={disabled}
          aria-disabled={disabled}
          aria-label={ariaLabel}
          aria-describedby={id ? `${id}-label` : undefined}
          tabIndex={disabled ? -1 : 0}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        >
          <IconWrapper aria-hidden="true">
            <CalendarIcon />
          </IconWrapper>
          <InputDisplay
            data-disabled={disabled ? 'true' : 'false'}
            id={id}
            data-show-placeholder={!date ? 'true' : 'false'}
          >
            {displayValue}
          </InputDisplay>
        </InputWrapper>
      </DateInputContainer>
    );
  },
);

DateInput.displayName = 'DateInput';

const VisuallyHidden = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`;

const DateInputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const InputWrapper = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  border: 0.5px solid var(--way-colors-borderColor);
  border-radius: var(--way-design-borderRadiusDefault);
  padding: 0 8px;
  background-color: ${({ disabled }) =>
    disabled ? 'var(--way-palette-black-10)' : 'transparent'};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${({ disabled }) =>
      disabled
        ? 'var(--way-palette-black-30)'
        : 'var(--way-colors-borderHoverColor)'};
  }

  &:focus-within {
    border-color: var(--way-palette-black-70);
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
`;

// Using data attributes to style in order to keep individual styles for each state
const InputDisplay = styled.div`
  border-bottom: none !important;
  width: 100%;
  height: 100%;
  font: inherit;
  font-size: 16px;
  line-height: 19px;
  padding: 17px 12px;
  border-radius: var(--way-design-borderRadiusDefault);
  background: transparent;
  border: none;
  min-height: 1px;
  font-weight: normal;
  font-style: normal;
  pointer-events: none; /* Prevent any interaction with this element */

  &[data-show-placeholder='true'],
  &[data-disabled='true'] {
    color: ${({ theme }) =>
      `var(--way-palette-${theme.mode === 'dark' ? 'white' : 'black'}-60)`};
  }

  &[data-show-placeholder='false']:not([data-disabled='true']) {
    color: var(--way-colors-inputColor);
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;

  svg {
    width: 20px;
    height: 20px;

    path,
    rect {
      stroke: var(--way-colors-svgColor);
    }
  }
`;

export default DateInput;
